<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader />
  </div>
  <div v-else class="view">
    <div class="monthly-budget">
      <div class="period-selectors">
        <select
          v-model="selectedMonth"
          class="ud-input ud-select form-select"
          @change="getMonthlyBudgetForMonthYear"
        >
          <option v-for="month in monthsData" :key="month" :value="month">
            {{ month.name }}
          </option>
        </select>
        <select
          v-model="selectedYear"
          class="ud-input ud-select form-select"
          @change="getMonthlyBudgetForMonthYear"
        >
          <option v-for="year in years" :key="year" :value="year">
            {{ year }}
          </option>
        </select>
      </div>

      <div class="budget-overview-grid">
        <div class="overview-card">
          <div class="text">Planned Income</div>
          <div class="value income-total">
            {{ formatMoney(Math.abs(totalIncomeBudget)) }}
          </div>
        </div>
        <div class="overview-card">
          <div class="text">Earned Income</div>
          <div class="value income-total">
            {{ formatMoney(Math.abs(totalIncome)) }}
          </div>
        </div>
        <div class="overview-card">
          <div class="text">Planned Expenses</div>
          <div
            class="value"
            :class="{ 'income-total': totalExpenseBudget < 0 }"
          >
            {{ formatMoney(Math.abs(totalExpenseBudget)) }}
          </div>
        </div>
        <div class="overview-card">
          <div class="text">Actual Expenses</div>
          <div class="value" :class="{ 'income-total': totalExpenses < 0 }">
            {{ formatMoney(Math.abs(totalExpenses)) }}
          </div>
        </div>
      </div>

      <div v-if="uncategorizedCount > 0" class="uncategorized-banner mt-2 mb-2">
        <div class="uncategorized-left">
          <div class="uncategorized-count-row">
            <strong>{{ uncategorizedCount }}</strong>
            {{
              uncategorizedCount === 1
                ? 'uncategorized transaction'
                : 'uncategorized transactions'
            }}
            totaling
            <strong>{{ formatMoney(uncategorizedAmount) }}</strong> this month.
          </div>
          <div class="uncategorized-sub-row">
            Included in Actual Expenses but not yet assigned to categories.
          </div>
        </div>
        <button class="uncategorized-btn" @click="goToUncategorized">
          Review
        </button>
      </div>

      <div class="chart-wrapper mx-auto mt-4">
        <canvas ref="chartCanvas"></canvas>
      </div>

      <div class="mt-4 mb-2">
        <span class="section-label">Expenses</span>
        <div
          v-for="(item, index) in budgetList"
          :key="item.categoryID || index"
          class="mb-1"
        >
          <div class="budget-cat-card" v-if="!item.income">
            <div class="budget-cat-row" @click="editCategoryBudget(item)">
              <div class="budget-cat-icon-wrap">
                <font-awesome-icon
                  :icon="
                    item.icon ? parseIconString(item.icon) : ['fas', 'tag']
                  "
                  :style="{ color: item.iconColor }"
                  style="font-size: 14px"
                />
              </div>
              <div class="budget-cat-name">{{ item.name }}</div>
              <div
                v-if="item.subcategories && item.subcategories.length > 0"
                class="budget-expand-btn"
                :class="{ expanded: showItemSubcategories[index] }"
                @click.stop="
                  showItemSubcategories[index] = !showItemSubcategories[index]
                "
              >
                <font-awesome-icon :icon="['fas', 'caret-down']" />
              </div>
            </div>

            <div class="budget-amounts" @click="editCategoryBudget(item)">
              <div class="budget-amount-col">
                <div class="budget-amount-value">
                  {{ formatMoney(item.budget) }}
                </div>
                <div class="budget-amount-label">Budget</div>
              </div>
              <div class="budget-amount-col">
                <div class="budget-amount-value">
                  {{ formatMoney(item.spent) }}
                </div>
                <div class="budget-amount-label">Spent</div>
              </div>
              <div class="budget-amount-col">
                <div class="budget-amount-value">
                  {{ formatMoney(item.available) }}
                </div>
                <div class="budget-amount-label">
                  {{ item.available < 0 ? 'Overspent' : 'Available' }}
                </div>
              </div>
            </div>

            <template
              v-if="item.subcategories && item.subcategories.length > 0"
            >
              <div
                class="budget-inc-sub-label"
                @click="editCategoryBudget(item)"
              >
                Incl. Subcategory Data
              </div>
              <div class="budget-amounts" @click="editCategoryBudget(item)">
                <div class="budget-amount-col">
                  <div class="budget-amount-value">
                    {{ formatMoney(item.incSubBudget) }}
                  </div>
                  <div class="budget-amount-label">Budget</div>
                </div>
                <div class="budget-amount-col">
                  <div class="budget-amount-value">
                    {{ formatMoney(item.incSubSpent) }}
                  </div>
                  <div class="budget-amount-label">Spent</div>
                </div>
                <div class="budget-amount-col">
                  <div class="budget-amount-value">
                    {{ formatMoney(item.incSubAvailable) }}
                  </div>
                  <div class="budget-amount-label">
                    {{ item.incSubAvailable < 0 ? 'Overspent' : 'Available' }}
                  </div>
                </div>
              </div>
            </template>

            <transition name="sub-slide">
              <div
                v-if="
                  item.subcategories &&
                  item.subcategories.length > 0 &&
                  showItemSubcategories[index]
                "
                class="budget-sub-list"
              >
                <div
                  v-for="(subcategory, subIndex) in item.subcategories"
                  :key="subIndex"
                  class="budget-sub-row"
                >
                  <div class="budget-sub-icon">
                    <font-awesome-icon
                      :icon="
                        subcategory.icon
                          ? parseIconString(subcategory.icon)
                          : ['fas', 'tag']
                      "
                      :style="{ color: subcategory.iconColor }"
                      style="font-size: 12px"
                    />
                  </div>
                  <div class="budget-sub-name">{{ subcategory.name }}</div>
                  <div class="budget-sub-amounts">
                    <div class="budget-amount-col">
                      <div class="budget-amount-value">
                        {{ formatMoney(subcategory.budget) }}
                      </div>
                      <div class="budget-amount-label">Budget</div>
                    </div>
                    <div class="budget-amount-col">
                      <div class="budget-amount-value">
                        {{ formatMoney(subcategory.spent) }}
                      </div>
                      <div class="budget-amount-label">Spent</div>
                    </div>
                    <div class="budget-amount-col">
                      <div class="budget-amount-value">
                        {{ formatMoney(subcategory.available) }}
                      </div>
                      <div class="budget-amount-label">
                        {{
                          subcategory.available < 0 ? 'Overspent' : 'Available'
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <div class="chart-wrapper mx-auto mt-4">
        <canvas ref="incomeChart"></canvas>
      </div>

      <div class="mt-4 mb-2">
        <span class="section-label">Income</span>
        <div
          v-for="(item, index) in budgetList"
          :key="'income-' + (item.categoryID || index)"
          class="mb-1"
        >
          <div class="budget-cat-card" v-if="item.income">
            <div class="budget-cat-row" @click="editCategoryBudget(item)">
              <div class="budget-cat-icon-wrap">
                <font-awesome-icon
                  :icon="
                    item.icon ? parseIconString(item.icon) : ['fas', 'tag']
                  "
                  :style="{ color: item.iconColor }"
                  style="font-size: 14px"
                />
              </div>
              <div class="budget-cat-name">{{ item.name }}</div>
              <div
                v-if="item.subcategories && item.subcategories.length > 0"
                class="budget-expand-btn"
                :class="{ expanded: showItemSubcategories['income-' + index] }"
                @click.stop="
                  showItemSubcategories['income-' + index] =
                    !showItemSubcategories['income-' + index]
                "
              >
                <font-awesome-icon :icon="['fas', 'caret-down']" />
              </div>
            </div>

            <div class="budget-amounts" @click="editCategoryBudget(item)">
              <div class="budget-amount-col">
                <div class="budget-amount-value">
                  {{ formatMoney(item.budget) }}
                </div>
                <div class="budget-amount-label">Budget</div>
              </div>
              <div class="budget-amount-col">
                <div class="budget-amount-value">
                  {{ formatMoney(item.spent) }}
                </div>
                <div class="budget-amount-label">Earned</div>
              </div>
              <div class="budget-amount-col">
                <div class="budget-amount-value">
                  {{ formatMoney(item.available) }}
                </div>
                <div class="budget-amount-label">
                  {{ item.available < 0 ? 'Extra' : 'Needed' }}
                </div>
              </div>
            </div>

            <template
              v-if="item.subcategories && item.subcategories.length > 0"
            >
              <div
                class="budget-inc-sub-label"
                @click="editCategoryBudget(item)"
              >
                Incl. Subcategory Data
              </div>
              <div class="budget-amounts" @click="editCategoryBudget(item)">
                <div class="budget-amount-col">
                  <div class="budget-amount-value">
                    {{ formatMoney(item.incSubBudget) }}
                  </div>
                  <div class="budget-amount-label">Budget</div>
                </div>
                <div class="budget-amount-col">
                  <div class="budget-amount-value">
                    {{ formatMoney(item.incSubSpent) }}
                  </div>
                  <div class="budget-amount-label">Earned</div>
                </div>
                <div class="budget-amount-col">
                  <div class="budget-amount-value">
                    {{ formatMoney(item.incSubAvailable) }}
                  </div>
                  <div class="budget-amount-label">
                    {{ item.incSubAvailable < 0 ? 'Extra' : 'Needed' }}
                  </div>
                </div>
              </div>
            </template>

            <transition name="sub-slide">
              <div
                v-if="
                  item.subcategories &&
                  item.subcategories.length > 0 &&
                  showItemSubcategories['income-' + index]
                "
                class="budget-sub-list"
              >
                <div
                  v-for="(subcategory, subIndex) in item.subcategories"
                  :key="subIndex"
                  class="budget-sub-row"
                >
                  <div class="budget-sub-icon">
                    <font-awesome-icon
                      :icon="
                        subcategory.icon
                          ? parseIconString(subcategory.icon)
                          : ['fas', 'tag']
                      "
                      :style="{ color: subcategory.iconColor }"
                      style="font-size: 12px"
                    />
                  </div>
                  <div class="budget-sub-name">{{ subcategory.name }}</div>
                  <div class="budget-sub-amounts">
                    <div class="budget-amount-col">
                      <div class="budget-amount-value">
                        {{ formatMoney(subcategory.budget) }}
                      </div>
                      <div class="budget-amount-label">Budget</div>
                    </div>
                    <div class="budget-amount-col">
                      <div class="budget-amount-value">
                        {{ formatMoney(subcategory.spent) }}
                      </div>
                      <div class="budget-amount-label">Earned</div>
                    </div>
                    <div class="budget-amount-col">
                      <div class="budget-amount-value">
                        {{ formatMoney(subcategory.available) }}
                      </div>
                      <div class="budget-amount-label">
                        {{ subcategory.available < 0 ? 'Extra' : 'Needed' }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick } from 'vue';
import { useMyStore } from '../store';
import { useRouter } from 'vue-router';
import { formatMoney } from '../utils/formatMoney.js';
import {
  Chart,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
Chart.register(DoughnutController, ArcElement, Tooltip, Legend);
import { MonthsData } from '../utils/constants.js';
import SkeletonLoader from './SkeletonLoader.vue';

export default {
  components: { SkeletonLoader },
  setup() {
    const store = useMyStore();
    const router = useRouter();
    const chartCanvas = ref(null);
    const incomeChart = ref(null);
    const selectedMonth = ref('');
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const monthsData = MonthsData;
    const showItemSubcategories = ref({});
    const selectedYear = ref('');
    const years = ref([]);
    const currentYear = currentDate.getFullYear();
    let budgetList = ref([]);
    let doughnutChart = null;
    let incomeDoughnutChart = null;
    let totalExpenseBudget = ref(0);
    let totalIncomeBudget = ref(0);
    let totalExpenses = ref(0);
    let totalIncome = ref(0);
    const loading = ref(true);
    const uncategorizedAmount = ref(0);
    const uncategorizedCount = ref(0);

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) mainElement.scrollTo(0, 0);
      }, 100);

      selectedMonth.value = monthsData.find(
        (month) => month.number - 1 === currentMonth
      );
      years.value = [currentYear - 1, currentYear, currentYear + 1];
      selectedYear.value = currentYear;

      store
        .createMonthlyBudget(selectedMonth.value.number, selectedYear.value)
        .then(() => {
          store
            .getMonthlyBudgetDataForAllCategories(
              selectedMonth.value.number,
              selectedYear.value
            )
            .then((response) => {
              loading.value = false;
              totalExpenseBudget.value = response.data.plannedExpenses;
              totalIncomeBudget.value = response.data.plannedIncome;
              totalExpenses.value = response.data.actualExpenses;
              totalIncome.value = response.data.actualIncome;
              budgetList.value = response.data.categoryBudgets;
              uncategorizedAmount.value = response.data.uncategorizedExpenses;
              uncategorizedCount.value =
                response.data.uncategorizedTransactionCount;
            });
        });
    });

    watch(budgetList, (newVal) => {
      if (newVal.length > 0) {
        createDoughnutChart();
        createIncomeDoughnutChart();
      }
    });

    const noDataPlugin = {
      id: 'noData',
      afterDraw: (chart) => {
        const { datasets } = chart.data;
        let hasData = datasets.some((d) => d.data.some((v) => v > 0));
        if (!hasData) {
          const {
            ctx,
            chartArea: { left, right, top, bottom },
          } = chart;
          ctx.save();
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.font = '16px Arial';
          ctx.fillStyle = store.darkTheme
            ? 'rgba(100,100,100,1)'
            : 'rgba(0,0,0,0.5)';
          ctx.fillText(
            'No data available',
            (left + right) / 2,
            (top + bottom) / 2
          );
          ctx.restore();
        }
      },
    };

    function createDoughnutChart() {
      nextTick(() => {
        if (doughnutChart) doughnutChart.destroy();
        const categoryGraphData = budgetList.value
          .filter((c) => !c.income)
          .map((c) => ({
            categoryLabel: c.name || 'Uncategorized',
            amount: c.incSubSpent,
            backgroundColor: getIconColor(c.categoryID),
          }));

        doughnutChart = new Chart(chartCanvas.value, {
          type: 'doughnut',
          data: {
            labels: categoryGraphData.map((i) => i.categoryLabel),
            datasets: [
              {
                data: categoryGraphData.map((i) => i.amount || 0),
                backgroundColor: categoryGraphData.map(
                  (i) => i.backgroundColor
                ),
              },
            ],
          },
          options: {
            plugins: {
              legend: { position: 'top' },
              title: {
                display: true,
                text: 'Spending by Category',
                font: { size: '20' },
              },
            },
          },
          plugins: [noDataPlugin],
        });
      });
    }

    function createIncomeDoughnutChart() {
      nextTick(() => {
        if (incomeDoughnutChart) incomeDoughnutChart.destroy();
        const categoryGraphData = budgetList.value
          .filter((c) => c.income)
          .map((c) => ({
            categoryLabel: c.name || 'Uncategorized',
            amount: c.incSubSpent,
            backgroundColor: getIconColor(c.categoryID),
          }));

        incomeDoughnutChart = new Chart(incomeChart.value, {
          type: 'doughnut',
          data: {
            labels: categoryGraphData.map((i) => i.categoryLabel),
            datasets: [
              {
                data: categoryGraphData.map((i) => i.amount || 0),
                backgroundColor: categoryGraphData.map(
                  (i) => i.backgroundColor
                ),
              },
            ],
          },
          options: {
            plugins: {
              legend: { position: 'top' },
              title: {
                display: true,
                text: 'Income by Category',
                font: { size: '20' },
              },
            },
          },
          plugins: [noDataPlugin],
        });
      });
    }

    const editCategoryBudget = (category) => {
      store
        .updateEditCategoryBudget(
          category,
          selectedMonth.value.number,
          selectedYear.value
        )
        .then(() => {
          router.push({ name: 'EditCategoryBudget' });
        });
    };

    const getMonthlyBudgetForMonthYear = () => {
      loading.value = true;
      store
        .createMonthlyBudget(selectedMonth.value.number, selectedYear.value)
        .then(() => {
          store
            .getMonthlyBudgetDataForAllCategories(
              selectedMonth.value.number,
              selectedYear.value
            )
            .then((response) => {
              totalExpenseBudget.value = response.data.plannedExpenses;
              totalIncomeBudget.value = response.data.plannedIncome;
              totalExpenses.value = response.data.actualExpenses;
              totalIncome.value = response.data.actualIncome;
              budgetList.value = response.data.categoryBudgets;
              uncategorizedAmount.value = response.data.uncategorizedExpenses;
              uncategorizedCount.value =
                response.data.uncategorizedTransactionCount;
              loading.value = false;
            });
        });
    };

    const parseIconString = (iconString) => {
      if (iconString) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return null;
    };

    const getIconColor = (categoryID) => {
      const category = store.categories.find(
        (x) => x.categoryID === categoryID
      );
      return category ? category.iconColor : '#7F77DD';
    };

    const goToUncategorized = () => {
      router.push({
        name: 'Transactions',
        query: {
          month: selectedMonth.value.number,
          year: selectedYear.value,
          onlyUncategorized: 'true',
        },
      });
    };

    return {
      store,
      router,
      chartCanvas,
      incomeChart,
      showItemSubcategories,
      editCategoryBudget,
      selectedMonth,
      monthsData,
      years,
      selectedYear,
      parseIconString,
      budgetList,
      getIconColor,
      getMonthlyBudgetForMonthYear,
      totalExpenseBudget,
      totalIncomeBudget,
      totalIncome,
      totalExpenses,
      formatMoney,
      loading,
      uncategorizedAmount,
      uncategorizedCount,
      goToUncategorized,
    };
  },
};
</script>

<style scoped>
.monthly-budget {
  animation: viewFadeIn 0.4s ease both;
}

.period-selectors {
  display: flex;
  gap: 10px;
  margin-bottom: 1rem;
}
.period-selectors .ud-input {
  flex: 1;
}

.budget-overview-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 1rem;
}

.uncategorized-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.85rem 1rem;
  border-radius: var(--radius-md);
  border-left: 4px solid var(--color-expense);
  background: rgba(216, 90, 48, 0.07);
  gap: 12px;
}
.dark-theme .uncategorized-banner {
  background: rgba(216, 90, 48, 0.12);
}
.uncategorized-left {
  flex: 1;
}
.uncategorized-count-row {
  font-size: 0.85rem;
  color: #213547;
  margin-bottom: 3px;
}
.dark-theme .uncategorized-count-row {
  color: #d4d2d2;
}
.uncategorized-sub-row {
  font-size: 0.75rem;
  color: #888;
}
.uncategorized-btn {
  background: var(--color-expense);
  border: none;
  color: white;
  padding: 0.45rem 1rem;
  border-radius: var(--radius-pill);
  font-size: 0.8rem;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity var(--transition-fast);
}
.uncategorized-btn:hover {
  opacity: 0.85;
}

.chart-wrapper {
  max-width: 340px;
  width: 100%;
}

.budget-cat-card {
  background: white;
  border-radius: var(--radius-lg);
  border: 1px solid #f0f0f0;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  padding: 0.85rem 1rem;
  cursor: pointer;
  transition:
    box-shadow var(--transition-base),
    transform var(--transition-base);
  animation: cardSlideUp 0.35s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.dark-theme .budget-cat-card {
  background: #212e3e;
  border-color: #2a3a4e;
}
.budget-cat-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

.budget-cat-row {
  display: grid;
  grid-template-columns: 36px 1fr auto;
  align-items: center;
  gap: 10px;
  margin-bottom: 0.65rem;
}
.budget-cat-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f4f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.dark-theme .budget-cat-icon-wrap {
  background: #1a2535;
}
.budget-cat-name {
  font-size: 0.88rem;
  font-weight: 600;
  color: #213547;
}
.dark-theme .budget-cat-name {
  color: white;
}

.budget-expand-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
  transition: transform 0.25s ease;
  cursor: pointer;
}
.budget-expand-btn.expanded {
  transform: rotate(180deg);
  color: var(--color-accent-purple);
}

.budget-amounts {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-top: 1px solid #f0f0f0;
  gap: 12px;
}
.dark-theme .budget-amounts {
  border-top-color: #2a3a4e;
}
.budget-amount-col {
  text-align: center;
  flex: 1 1 0;
  min-width: 0;
}
.budget-amount-value {
  font-size: 0.88rem;
  font-weight: 600;
  color: #213547;
  white-space: nowrap;
}
.dark-theme .budget-amount-value {
  color: #d4d2d2;
}
.budget-amount-label {
  font-size: 0.7rem;
  color: #888;
  margin-top: 2px;
}

.budget-inc-sub-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--color-primary);
  padding: 0.4rem 0 0.2rem;
}
.dark-theme .budget-inc-sub-label {
  color: var(--color-accent-teal);
}

.budget-sub-list {
  border-top: 1px solid #f0f0f0;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
}
.dark-theme .budget-sub-list {
  border-top-color: #2a3a4e;
}
.budget-sub-row {
  display: grid;
  grid-template-columns: 28px 1fr auto;
  align-items: start;
  gap: 8px;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f8f8f8;
  min-width: 0;
}
.dark-theme .budget-sub-row {
  border-bottom-color: #2a3a4e;
}
.budget-sub-row:last-child {
  border-bottom: none;
}
.budget-sub-icon {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #f4f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dark-theme .budget-sub-icon {
  background: #1a2535;
}
.budget-sub-name {
  font-size: 0.78rem;
  font-weight: 500;
  color: #555;
  padding-top: 4px;
}
.dark-theme .budget-sub-name {
  color: #aaa;
}
.budget-sub-amounts {
  display: flex;
  gap: 8px;
  min-width: 0;
}
.budget-sub-amounts .budget-amount-col {
  min-width: 50px;
  flex: 0 0 auto;
}

.icon-wrapper svg {
  display: block;
  position: relative;
  line-height: 30px;
}

.dark-theme .ud-input {
  border: 1.5px solid #e7e7e7;
}

@media screen and (min-width: 992px) {
  .chart-wrapper {
    max-width: 400px;
  }
}
</style>

<!-- <template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader />
  </div>
  <div v-else>
    <div class="mt-1 flex w-lg-50">
      <div class="w-40">
        <select
          v-model="selectedMonth"
          class="form-select grey-form-select"
          @change="getMonthlyBudgetForMonthYear"
        >
          <option v-for="month in monthsData" :key="month" :value="month">
            {{ month.name }}
          </option>
        </select>
      </div>
      <div class="ms-1 w-25">
        <select
          v-model="selectedYear"
          class="form-select grey-form-select"
          @change="getMonthlyBudgetForMonthYear"
        >
          <option v-for="year in years" :key="year" :value="year">
            {{ year }}
          </option>
        </select>
      </div>
    </div>
    <div class="income-overview mt-1">
      <div class="overview-card">
        <div class="text">Planned Income</div>
        <div class="value income-total">{{ formatMoney(Math.abs(totalIncomeBudget)) }}</div>
      </div>
      <div class="overview-card">
        <div class="text">Earned Income</div>
        <div class="value income-total">{{ formatMoney(Math.abs(totalIncome)) }}</div>
      </div>
      <div class="overview-card">
        <div class="text">Planned Expenses</div>
        <div class="value" :class="{ 'income-total': totalExpenseBudget < 0 }">{{ formatMoney(Math.abs(totalExpenseBudget)) }}</div>
      </div>
      <div class="overview-card">
        <div class="text">Actual Expenses</div>
        <div class="value" :class="{ 'income-total': totalExpenses < 0 }">{{ formatMoney(Math.abs(totalExpenses)) }}</div>
      </div>
    </div>
    <div
      v-if="uncategorizedCount > 0"
      class="uncategorized-banner mt-3 mb-3"
    >
      <div class="left">
        <div class="count-row">
          <strong>{{ uncategorizedCount }}</strong>
          {{ uncategorizedCount === 1
            ? 'uncategorized transaction'
            : 'uncategorized transactions' }}
          totaling
          <strong>{{ formatMoney(uncategorizedAmount) }}</strong>
          this month.
        </div>

        <div class="sub-row">
          These are included in your Actual Expenses but not yet assigned to categories.
        </div>
      </div>

      <div class="right">
        <button class="assign-btn" @click="goToUncategorized">
          Review
        </button>
      </div>
    </div>
    <div class="mt-4 pt-2 pb-2 budget-chart-wrapper mx-auto">
      <canvas ref="chartCanvas"></canvas>
    </div>
    <div class="mt-4 mb-2">
      <div v-for="(item, index) in budgetList" :key="item" class="mb-2">
        <div class="category-list align-center pointer" v-if="!item.income">
          <div class="icon" @click="editCategoryBudget(item)">
            <span class="icon-wrapper">
              <font-awesome-icon
                :icon="item.icon ? parseIconString(item.icon) : ['fas', 'tag']"
                :style="{ color: item.iconColor }"
              ></font-awesome-icon
            ></span>
          </div>
          <div class="category-label" @click="editCategoryBudget(item)">
            {{ item.name }}
          </div>
          <div
            class="text-right"
            v-if="item.subcategories && item.subcategories.length > 0"
            @click="
              showItemSubcategories[index] = !showItemSubcategories[index]
            "
          >
            <font-awesome-icon :icon="['fas', 'caret-down']" />
          </div>
          <div class="category-budget-info" @click="editCategoryBudget(item)">
            <div>
              <div class="category-budget-amount">
                {{ formatMoney(item.budget) }}
              </div>
              <div class="category-budget-label">Budget</div>
            </div>
            <div>
              <div class="category-budget-amount">
                {{ formatMoney(item.spent) }}
              </div>
              <div class="category-budget-label">Spent</div>
            </div>
            <div>
              <div class="category-budget-amount">
                {{ formatMoney(item.available) }}
              </div>
              <div class="category-budget-label">
                {{ item.available < 0 ? 'Overspent' : 'Available' }}
              </div>
            </div>
          </div>
          <div
            v-if="item.subcategories && item.subcategories.length > 0"
            class="mt-3 subcategory-data-label"
            @click="editCategoryBudget(item)"
          >
            Incl. Subcategory Data
          </div>
          <div
            v-if="item.subcategories && item.subcategories.length > 0"
            class="category-budget-info"
            @click="editCategoryBudget(item)"
          >
            <div>
              <div class="category-budget-amount">
                {{ formatMoney(item.incSubBudget) }}
              </div>
              <div class="category-budget-label">Budget</div>
            </div>
            <div>
              <div class="category-budget-amount">
                {{ formatMoney(item.incSubSpent) }}
              </div>
              <div class="category-budget-label">Spent</div>
            </div>
            <div>
              <div class="category-budget-amount">
                {{ formatMoney(item.incSubAvailable) }}
              </div>
              <div class="category-budget-label">
                {{ item.incSubAvailable < 0 ? 'Overspent' : 'Available' }}
              </div>
            </div>
          </div>
          <div
            v-if="item.subcategories && item.subcategories.length > 0"
            class="subcategory-area"
            @click="editCategoryBudget(item)"
          >
            <div v-if="showItemSubcategories[index]">
              <div
                v-for="(subcategory, index) in item.subcategories"
                :key="index"
              >
                <div class="subcategory-list align-center">
                  <div class="icon">
                    <span class="icon-wrapper"
                      ><font-awesome-icon
                        :icon="
                          subcategory.icon
                            ? parseIconString(subcategory.icon)
                            : ['fas', 'tag']
                        "
                        :style="{ color: subcategory.iconColor }"
                      ></font-awesome-icon
                    ></span>
                  </div>
                  <div>{{ subcategory.name }}</div>
                  <div class="category-budget-info">
                    <div>
                      <div class="category-budget-amount">
                        {{ formatMoney(subcategory.budget) }}
                      </div>
                      <div class="category-budget-label">Budget</div>
                    </div>
                    <div>
                      <div class="category-budget-amount">
                        {{ formatMoney(subcategory.spent) }}
                      </div>
                      <div class="category-budget-label">Spent</div>
                    </div>
                    <div>
                      <div class="category-budget-amount">
                        {{ formatMoney(subcategory.available) }}
                      </div>
                      <div class="category-budget-label">
                        {{
                          subcategory.available < 0 ? 'Overspent' : 'Available'
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-4 pt-2 pb-2 budget-chart-wrapper mx-auto">
      <canvas ref="incomeChart"></canvas>
    </div>
    <div class="mt-4 mb-2">
      <div v-for="(item, index) in budgetList" :key="item" class="mb-2">
        <div class="category-list align-center pointer" v-if="item.income">
          <div class="icon" @click="editCategoryBudget(item)">
            <span class="icon-wrapper">
              <font-awesome-icon
                :icon="item.icon ? parseIconString(item.icon) : ['fas', 'tag']"
                :style="{ color: item.iconColor }"
              ></font-awesome-icon
            ></span>
          </div>
          <div class="category-label" @click="editCategoryBudget(item)">
            {{ item.name }}
          </div>
          <div
            class="text-right"
            v-if="item.subcategories && item.subcategories.length > 0"
            @click="
              showItemSubcategories[index] = !showItemSubcategories[index]
            "
          >
            <font-awesome-icon :icon="['fas', 'caret-down']" />
          </div>
          <div class="category-budget-info" @click="editCategoryBudget(item)">
            <div>
              <div class="category-budget-amount">
                {{ formatMoney(item.budget) }}
              </div>
              <div class="category-budget-label">Budget</div>
            </div>
            <div>
              <div class="category-budget-amount">
                {{ formatMoney(item.spent) }}
              </div>
              <div class="category-budget-label">Earned</div>
            </div>
            <div>
              <div class="category-budget-amount">
                {{ formatMoney(item.available) }}
              </div>
              <div class="category-budget-label">
                {{ item.available < 0 ? 'Extra' : 'Needed' }}
              </div>
            </div>
          </div>
          <div
            v-if="item.subcategories && item.subcategories.length > 0"
            class="mt-3 subcategory-data-label"
            @click="editCategoryBudget(item)"
          >
            Incl. Subcategory Data
          </div>
          <div
            v-if="item.subcategories && item.subcategories.length > 0"
            class="category-budget-info"
            @click="editCategoryBudget(item)"
          >
            <div>
              <div class="category-budget-amount">
                {{ formatMoney(item.incSubBudget) }}
              </div>
              <div class="category-budget-label">Budget</div>
            </div>
            <div>
              <div class="category-budget-amount">
                {{ formatMoney(item.incSubSpent) }}
              </div>
              <div class="category-budget-label">Earned</div>
            </div>
            <div>
              <div class="category-budget-amount">
                {{ formatMoney(item.incSubAvailable) }}
              </div>
              <div class="category-budget-label">
                {{ item.incSubAvailable < 0 ? 'Extra' : 'Needed' }}
              </div>
            </div>
          </div>
          <div
            v-if="item.subcategories && item.subcategories.length > 0"
            class="subcategory-area"
            @click="editCategoryBudget(item)"
          >
            <div v-if="showItemSubcategories[index]">
              <div
                v-for="(subcategory, index) in item.subcategories"
                :key="index"
              >
                <div class="subcategory-list align-center">
                  <div class="icon">
                    <span class="icon-wrapper"
                      ><font-awesome-icon
                        :icon="
                          subcategory.icon
                            ? parseIconString(subcategory.icon)
                            : ['fas', 'tag']
                        "
                        :style="{ color: subcategory.iconColor }"
                      ></font-awesome-icon
                    ></span>
                  </div>
                  <div>{{ subcategory.name }}</div>
                  <div class="category-budget-info">
                    <div>
                      <div class="category-budget-amount">
                        {{ formatMoney(subcategory.budget) }}
                      </div>
                      <div class="category-budget-label">Budget</div>
                    </div>
                    <div>
                      <div class="category-budget-amount">
                        {{ formatMoney(subcategory.spent) }}
                      </div>
                      <div class="category-budget-label">Earned</div>
                    </div>
                    <div>
                      <div class="category-budget-amount">
                        {{ formatMoney(subcategory.available) }}
                      </div>
                      <div class="category-budget-label">
                        {{ subcategory.available < 0 ? 'Extra' : 'Needed' }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick } from 'vue';
import { useMyStore } from '../store';
import { useRouter } from 'vue-router';
import { formatMoney } from '../utils/formatMoney.js';

import {
  Chart,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
Chart.register(DoughnutController, ArcElement, Tooltip, Legend);
import { MonthsData } from '../utils/constants.js';
import SkeletonLoader from '../components/SkeletonLoader.vue';

export default {
  components: { SkeletonLoader },
  setup() {
    const store = useMyStore();
    const router = useRouter();
    const chartCanvas = ref(null);
    const incomeChart = ref(null);
    const sliderOptions = [
      { label: 'Monthly', disabled: false },
      { label: 'Annual', disabled: true },
    ];
    const selectedMonth = ref('');
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const monthsData = MonthsData;
    const showItemSubcategories = ref({});
    const selectedYear = ref('');
    const years = ref([]);
    const currentYear = currentDate.getFullYear();
    let budgetList = ref([]);
    let doughnutChart = null;
    let incomeDoughnutChart = null;
    let totalExpenseBudget = ref(0);
    let totalIncomeBudget = ref(0);
    let totalExpenses = ref(0);
    let totalIncome = ref(0);
    const loading = ref(true);
    const uncategorizedAmount = ref(0);
    const uncategorizedCount = ref(0);

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) {
          mainElement.scrollTo(0, 0);
        }
      }, 100);

      selectedMonth.value = monthsData.find(
        (month) => month.number - 1 === currentMonth
      );
      years.value = [currentYear - 1, currentYear, currentYear + 1];
      selectedYear.value = currentYear;

      store
        .createMonthlyBudget(selectedMonth.value.number, selectedYear.value)
        .then(() => {
          store
            .getMonthlyBudgetDataForAllCategories(
              selectedMonth.value.number,
              selectedYear.value
            )
            .then((response) => {
              loading.value = false;
              totalExpenseBudget.value = response.data.plannedExpenses;
              totalIncomeBudget.value = response.data.plannedIncome;
              totalExpenses.value = response.data.actualExpenses;
              totalIncome.value = response.data.actualIncome;
              budgetList.value = response.data.categoryBudgets;
              uncategorizedAmount.value = response.data.uncategorizedExpenses;
              uncategorizedCount.value = response.data.uncategorizedTransactionCount;
            });
        });
    });

    watch(budgetList, (newVal) => {
      if (newVal.length > 0) {
        createDoughnutChart();
        createIncomeDoughnutChart();
      }
    });

    const noDataPlugin = {
      id: 'noData',
      afterDraw: (chart) => {
        const { datasets } = chart.data;
        let hasData = false;

        for (let i = 0; i < datasets.length; i++) {
          if (datasets[i].data.some((data) => data > 0)) {
            hasData = true;
            break;
          }
        }

        if (!hasData) {
          const {
            ctx,
            chartArea: { left, right, top, bottom },
          } = chart;
          ctx.save();
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.font = '16px Arial';
          ctx.fillStyle = store.darkTheme
            ? 'rgba(100, 100, 100, 1)'
            : 'rgba(0, 0, 0, 0.5)';
          ctx.fillText(
            'No data available',
            (left + right) / 2,
            (top + bottom) / 2
          );
          ctx.restore();
        }
      },
    };

    function createDoughnutChart() {
      nextTick(() => {
        if (doughnutChart) {
          doughnutChart.destroy();
        }
        const categoryGraphData = [];
        budgetList.value.forEach((category) => {
          if (!category.income) {
            let categoryData = {
              categoryLabel: category.name ? category.name : 'Uncategorized',
              amount: category.incSubSpent,
              backgroundColor: getIconColor(category.categoryID),
            };
            categoryGraphData.push(categoryData);
          }
        });

        doughnutChart = new Chart(chartCanvas.value, {
          type: 'doughnut',
          data: {
            labels: categoryGraphData.map((item) => item.categoryLabel),
            datasets: [
              {
                data: categoryGraphData.map((item) =>
                  item.amount ? item.amount : 0
                ),
                backgroundColor: categoryGraphData.map(
                  (item) => item.backgroundColor
                ),
              },
            ],
          },
          options: {
            plugins: {
              legend: {
                position: 'top',
              },
              title: {
                display: true,
                text: 'Spending by Category',
                font: {
                  size: '26',
                },
              },
            },
          },
          plugins: [noDataPlugin],
        });
      });
    }

    function createIncomeDoughnutChart() {
      nextTick(() => {
        if (incomeDoughnutChart) {
          incomeDoughnutChart.destroy();
        }
        const categoryGraphData = [];
        budgetList.value.forEach((category) => {
          if (category.income) {
            let categoryData = {
              categoryLabel: category.name ? category.name : 'Uncategorized',
              amount: category.incSubSpent,
              backgroundColor: getIconColor(category.categoryID),
            };
            categoryGraphData.push(categoryData);
          }
        });

        incomeDoughnutChart = new Chart(incomeChart.value, {
          type: 'doughnut',
          data: {
            labels: categoryGraphData.map((item) => item.categoryLabel),
            datasets: [
              {
                data: categoryGraphData.map((item) =>
                  item.amount ? item.amount : 0
                ),
                backgroundColor: categoryGraphData.map(
                  (item) => item.backgroundColor
                ),
              },
            ],
          },
          options: {
            plugins: {
              legend: {
                position: 'top',
              },
              title: {
                display: true,
                text: 'Income by Category',
                font: {
                  size: '26',
                },
              },
            },
          },
          plugins: [noDataPlugin],
        });
      });
    }

    const editCategoryBudget = (category) => {
      store
        .updateEditCategoryBudget(
          category,
          selectedMonth.value.number,
          selectedYear.value
        )
        .then(() => {
          router.push({ name: 'EditCategoryBudget' });
        });
    };

    const getTransactionsForMonthYear = () => {
      let transactionsRequest = {
        StartDate: `${selectedYear.value}-${selectedMonth.value.number}-${
          selectedMonth.value.firstDay
        }`,
        EndDate: `${selectedYear.value}-${selectedMonth.value.number}-${
          selectedMonth.value.lastDay
        }`,
      };
      store.getTransactions(transactionsRequest);
    };

    const getMonthlyBudgetForMonthYear = () => {
      store
        .createMonthlyBudget(selectedMonth.value.number, selectedYear.value)
        .then(() => {
          store
            .getMonthlyBudgetDataForAllCategories(
              selectedMonth.value.number,
              selectedYear.value
            )
            .then((response) => {
              totalExpenseBudget.value = response.data.plannedExpenses;
              totalIncomeBudget.value = response.data.plannedIncome;
              totalExpenses.value = response.data.actualExpenses;
              totalIncome.value = response.data.actualIncome;
              budgetList.value = response.data.categoryBudgets;
              uncategorizedAmount.value = response.data.uncategorizedExpenses;
              uncategorizedCount.value = response.data.uncategorizedTransactionCount;
            });
        });
    };

    const parseIconString = (iconString) => {
      if (iconString) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return null;
    };

    const getIcon = (categoryID) => {
      const category = store.categories.find(
        (x) => x.categoryID === categoryID
      );
      return category ? parseIconString(category.icon) : ['fas', 'tag'];
    };

    const getIconColor = (categoryID) => {
      const category = store.categories.find(
        (x) => x.categoryID === categoryID
      );
      return category ? category.iconColor : '#000000';
    };

    const getSubcategoryIcon = (subcategoryID) => {
      for (const category of store.categories) {
        if (category.subcategories) {
          const subcategory = category.subcategories.find(
            (x) => x.subcategoryID === subcategoryID
          );
          if (subcategory) {
            return parseIconString(subcategory.icon);
          }
        }
      }
      return ['fas', 'tag'];
    };

    const goToUncategorized = () => {
      router.push({
        name: 'Transactions',
        query: {
          month: selectedMonth.value.number,
          year: selectedYear.value,
          onlyUncategorized: 'true',
        },
      });
    };

    return {
      store,
      router,
      sliderOptions,
      chartCanvas,
      createDoughnutChart,
      showItemSubcategories,
      editCategoryBudget,
      selectedMonth,
      getTransactionsForMonthYear,
      months,
      monthsData,
      years,
      selectedYear,
      parseIconString,
      budgetList,
      getIcon,
      getIconColor,
      getMonthlyBudgetForMonthYear,
      doughnutChart,
      getSubcategoryIcon,
      totalExpenseBudget,
      totalIncomeBudget,
      totalIncome,
      totalExpenses,
      formatMoney,
      createIncomeDoughnutChart,
      incomeDoughnutChart,
      incomeChart,
      loading,
      uncategorizedAmount,
      uncategorizedCount,
      goToUncategorized
    };
  },
};
</script>

<style scoped>
.income-overview {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5em;
}

.icon-wrapper svg {
  display: block;
  position: relative;
  text-align: center;
  line-height: 30px;
}

.category-budget-info {
  display: flex;
  justify-content: space-between;
  grid-column: 2 / span 1;
  padding-top: 0.25em;
  flex-wrap: wrap;
}

.category-budget-amount {
  font-weight: 500;
  font-size: 0.9em;
}

.category-budget-label {
  font-size: 0.8em;
}

.light-theme .category-budget-label {
  color: #515a65;
}

.dark-theme .category-budget-label {
  color: #d4d2d2;
}

.subcategory-data-label {
  grid-column: 2 / span 2;
  font-size: 0.8em;
}

.light-theme .subcategory-data-label {
  color: #004751;
}

.dark-theme .subcategory-data-label {
  color: #6897cf;
}

.uncategorized-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75em 1em;
  border-radius: 10px;
  border-left: 5px solid #ff6666;
}

.light-theme .uncategorized-banner {
  background: #ffecec;
  color: #4a4a4a;
}

.dark-theme .uncategorized-banner {
  background: #3a2d2d;
  color: #fff;
}

.uncategorized-banner .left {
  flex: 1;
}

.uncategorized-banner .count-row {
  font-size: 0.93em;
  margin-bottom: 0.25em;
}

.uncategorized-banner .sub-row {
  font-size: 0.8em;
  opacity: 0.8;
}

.assign-btn {
  background: #ff6666;
  border: none;
  color: white;
  padding: 0.45em 1em;
  border-radius: 8px;
  font-size: 0.85em;
  cursor: pointer;
}

.assign-btn:hover {
  background: #ff4a4a;
}

/* .light-theme .income-total {
  color: #86c24c;
}

.dark-theme .income-total {
  color: #89ca50;
} */

@media screen and (min-width: 992px) {
  .budget-chart-wrapper {
    width: 80%;
    margin: 0 auto;
  }
}

@media screen and (min-width: 1200px) {
  .budget-chart-wrapper {
    width: 40%;
    margin: 0 auto;
  }
}
</style> -->
